<template>
  <div class="input-panel panel-facility">
    <button 
      class="btn btn-sm btn-icon pad-left-0" 
      @click="goBack" 
    ><i class="fa fa-chevron-circle-left"></i>&nbsp;Back</button>

    <div class="panel-facility__meta" v-show="currentStep == 'meta'">
      <h2 class="input-panel__head input-panel__head--align-l mrgn-btm-15">Custom Profile</h2>

      <div>
        <label for="custom-profile-category">Categorize the Custom Profile:</label>
        <select id="custom-profile-category"
          v-model="profileCategory.tier1"
          @change="clearProfileCategoryTier2"
        >
          <option disabled value="">Select Category</option>
          <option 
            v-for="(category, index) in facilityProfiles"
            v-html="cleanCategory(category.name)"
            :value="{ 
              name: cleanCategory(category.name), 
              displayName: category.displayName, 
              id: category.id,
              index
            }"
          ></option>
        </select>

        <template v-if="profileCategory.tier1.id && profileCategory.tier1.name && facilityProfiles[profileCategory.tier1.index]">

          <div class="panel-facility__data-selection-list">
            <profile-options
              :index="0"
              :profile="facilityProfiles[profileCategory.tier1.index]"
              :selections="profileCategory.tier2"
              @bus="profileOptionsBus"
            ></profile-options>
          </div>

        </template>
      </div>

      <template v-if="profileName || Object.keys(profileCategory.tier2).length > 0">
        <form>
          <label for="custom-profile-name">Enter a name for this Custom Profile (Max {{ maxNameLength }} char.)</label>
          <input id="custom-profile-name" placeholder="Optional Price Request Name" 
            @input="limitNameLength"
            ref="custom-profile-name" 
            v-model="profileName"
          >
          <button class="btn btn-sm" 
            @click.prevent="setFacilityProfileMeta"
            :disabled="!profileCategory.tier2.facilityType || !profileCategory.tier2.facilityType.id"
          >Enter</button> 
        </form>
      </template>
    </div> <!-- .panel-facility__meta -->

    <div class="pad-btm-30 mrgn-btm-30" v-show="currentStep == 'profile'">
      <div class="panel-facility__header">
        <h2 class="input-panel__head input-panel__head--align-l panel-facility__head mrgn-btm-15">Custom Profile</h2>

          <p class="mrgn-btm-5">Enter monthly values.</p>

          <div class="panel-facility__buttons">
            <button type="submit" class="btn btn-sm mrgn-btm-0 panel-facility__submit" 
              @click.prevent="clearCustomFacilityProfile"
              :disabled="!customFacilityProfileEdited"
            >Clear</button>
            <button type="submit" class="btn btn-sm mrgn-btm-0 panel-facility__submit" 
              :disabled="!activeButton"
              @click.prevent="setCustomFacilityProfile()"
            >Submit Profile</button>
          </div>
      </div>

      <div class="custom-facility__repeat-values-options" v-if="longestTermLength > 12">
        <input type="checkbox" v-model="repeatMonthlyValues" id="repeat-monthly-values">
        <label for="repeat-monthly-values" class="inline">Repeat monthly values beyond the first year</label>
      </div>

      <div class="custom-facility__enter-on-peak-usage-percent">
        <input 
          type="checkbox" 
          id="enter-on-peak-usage-percent"
          ref="enter-on-peak-usage-percent"
          :value="enterOnPeakUsagePercent" 
          @change="event => setOnPeakUsagePercentSelection(event.target.checked ? true : false)"
        >
        <label for="enter-on-peak-usage-percent" class="inline">
          Auto-populate On-Peak Usage as a Percentage 
          <span v-show="! isLegacyCustomProfile && ! hasChangedAnOnPeakUsagePercentValue" class="has-color-normal">(based on selected profile)</span>
        </label>
      </div>

      <div class="panel-facility__table-wrap">
        <div class="custom-facility-table custom-facility-table--header">
          <div class="custom-facility-table__col custom-facility-table__col--header">
            <!-- INDEX -->
            <div class="custom-facility-table__cell custom-facility-table__cell--xsm custom-facility-table__cell--sm custom-facility-table__cell--header">&nbsp;</div> 
          </div>

          <div class="custom-facility-table__col custom-facility-table__col--header">
            <!-- MONTH -->
            <div class="custom-facility-table__cell custom-facility-table__cell--sm  custom-facility-table__cell--header">Month</div>
          </div>

          <div class="custom-facility-table__col">
            <!-- TOTAL USAGE -->
            <div class="custom-facility-table__cell"><b>Total Usage <br>(kWh)</b></div>
          </div>

          <div class="custom-facility-table__col" v-show="enterOnPeakUsagePercent">
            <!-- ON-PEAK USAGE % -->
            <div class="custom-facility-table__cell"><b>On-Peak Usage <br>(%)</b></div>
          </div>

          <div class="custom-facility-table__col">
            <!-- ON-PEAK USAGE -->
            <div class="custom-facility-table__cell"><b>On-Peak Usage <br>(kWh)</b></div>
          </div>

          <div class="custom-facility-table__col">
            <!-- OFF-PEAK USAGE -->
            <div class="custom-facility-table__cell"><b>Off-Peak Usage <br>(kWh)</b></div>
          </div>

          <div class="custom-facility-table__col">
            <!-- PEAK DEMAND -->
            <div class="custom-facility-table__cell"><b>Peak Demand <br>(KW)</b></div>
          </div>

          <template v-for="column in extraColumns">
            <div class="custom-facility-table__col">
              <div class="custom-facility-table__cell">
                <b v-html="wrapUnits(column.name)"></b>
              </div>
            </div>
          </template>
        </div>

        <div class="custom-facility"
          :class="{ 'custom-facility--shallow': collapseCustomFacilityProfileTable }"
        >
          <div class="custom-facility-table">
            <div class="custom-facility-table__col custom-facility-table__col--header">
              <!-- INDEX -->
              <div class="custom-facility-table__cell custom-facility-table__cell--xsm custom-facility-table__cell--sm custom-facility-table__cell--header" v-for="(item, index) in profile">{{ index + 1 }}</div>
            </div>

            <div class="custom-facility-table__col custom-facility-table__col--header">
              <!-- MONTH -->
              <div class="custom-facility-table__cell custom-facility-table__cell--sm custom-facility-table__cell--header" v-for="month in profile">{{ displayMonth(month.month) }}</div>
            </div>

            <div class="custom-facility-table__col">
              <!-- TOTAL USAGE -->
              <div class="custom-facility-table__cell" v-for="(month, index) in profile">
                <input type="text"
                  class="numeric-input"
                  :ref="'totalUsage-' + index"
                  @input="setUsage(index, $event.target.value)"
                  @blur="setUsage(index, $event.target.value)"
                  :readonly="inputDisabled(index, 'usage')"
                  :tabindex="tabIndex(index)"
                >
              </div>
            </div>

            <div class="custom-facility-table__col" v-show="enterOnPeakUsagePercent">
              <!-- ON-PEAK USAGE % -->
              <div class="custom-facility-table__cell" v-for="(month, index) in profile">
                <input type="text"
                  class="numeric-input"
                  :ref="'onPeakUsagePercent-' + index"
                  @input="setOnPeakUsagePercent(index, $event.target.value)"
                  @blur="setOnPeakUsagePercent(index, $event.target.value)"
                  :readonly="inputDisabled(index, 'on-peak-usage-percent')"
                  :tabindex="tabIndex(index)"
                  maxlength="3" 
                >
              </div>
            </div>

            <div class="custom-facility-table__col">
              <!-- ON-PEAK USAGE -->
              <div class="custom-facility-table__cell" v-for="(month, index) in profile">
                <input type="text"
                  class="numeric-input"
                  :ref="'onPeakUsage-' + index"
                  @input="setOnPeakUsage(index, $event.target.value)"
                  @blur="setOnPeakUsage(index, $event.target.value)"
                  :readonly="inputDisabled(index, 'on-peak-usage')"
                  :tabindex="tabIndex(index)"
                >
              </div>
            </div>

            <div class="custom-facility-table__col">
              <!-- OFF-PEAK USAGE -->
              <div class="custom-facility-table__cell" v-for="(month, index) in calculatedProfile">
                <template v-if="profile[index].onPeakUsage || profile[index].onPeakUsage === 0">
                  {{ displayValue(calculatedProfile[index].offPeakUsage) }}
                </template>
              </div>
            </div>

            <div class="custom-facility-table__col">
              <!-- PEAK DEMAND -->
              <div class="custom-facility-table__cell" v-for="(month, index) in profile">
                <input type="text"
                  :ref="'peakDemand-' + index"
                  class="numeric-input-decimal"
                  @input="setPeakDemand(index, $event.target.value)"
                  @blur="validatePeakDemand(index, $event.target.value, true)"
                  :readonly="inputDisabled(index, 'on-peak-demand')"
                  :tabindex="tabIndex(index)"
                >
              </div>
            </div>

            <template v-for="column in extraColumns">
              <div class="custom-facility-table__col">
                <div class="custom-facility-table__cell" v-for="(month, index) in profile">
                  <input type="text"
                    :ref="'extra_' + column.id + '-' + index"
                    class="numeric-input-decimal"
                    :tabindex="tabIndex(index)"
                    @input="updateExtraColumnValue(column.id, index, $event)"
                  >
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="custom-facility-table custom-facility-table--footer">
          <div class="custom-facility-table__col custom-facility-table__col--header">
            <!-- INDEX -->
            <div class="custom-facility-table__cell custom-facility-table__cell--xsm custom-facility-table__cell--sm custom-facility-table__cell--header-collapse"
              @click="toggleTableCollapse"
            >
              <i class="fa fa-angle-double-down" v-show="collapseCustomFacilityProfileTable"></i>
              <i class="fa fa-angle-double-up" v-show="!collapseCustomFacilityProfileTable"></i>
            </div> 
          </div>

          <div class="custom-facility-table__col custom-facility-table__col--header">
            <!-- MONTH -->
            <div class="custom-facility-table__cell custom-facility-table__cell--sm custom-facility-table__cell--header">Total</div>
          </div>

          <div class="custom-facility-table__col">
            <!-- TOTAL USAGE -->
            <div class="custom-facility-table__cell">{{ displayValue(total.usage) }}</div> 
          </div>

          <div class="custom-facility-table__col" v-show="enterOnPeakUsagePercent">
            <!-- ON-PEAK USAGE % -->
            <div class="custom-facility-table__cell"></div> 
          </div>

          <div class="custom-facility-table__col">
            <!-- ON-PEAK USAGE -->
            <div class="custom-facility-table__cell">{{ displayValue(total.onPeakUsage) }}</div> 
          </div>

          <div class="custom-facility-table__col">
            <!-- OFF-PEAK USAGE -->
            <div class="custom-facility-table__cell">{{ displayValue(total.offPeakUsage) }}</div>
          </div>

          <div class="custom-facility-table__col">
            <!-- PEAK DEMAND -->
            <div class="custom-facility-table__cell">N/A</div>
          </div>

          <template v-for="column in extraColumns">
            <div class="custom-facility-table__col">
              <div class="custom-facility-table__cell custom-facility-table__cell--with-button">
                <button class="btn btn-lrg btn-icon label__button"
                  @click.prevent="removeColumn(column.id)"
                  title="Remove Column"
                ><i class="fa fa-times-circle"></i>&nbsp;Remove</button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="custom-facility__row-options clearfix">
        <div class="custom-facility__row-options__col custom-facility__row-options__col--a">
          <template v-for="(column, index) in customFacilityProfileColumns">
            <custom-column 
                v-if="index === 0 || index === 2"
                :key="column.id"
                :id="column.id"
                :index="index"
                :name="column.name"
                :abbrev="column.abbrev"
                :options="column.options"
                :existing-columns="extraColumns"
                :ref="'custom-column-' + column.id"
                @addColumn="addColumn"
                @updateColumn="updateColumn"
                @removeColumn="removeColumn"
                @shallowTable="collapseTable"
              />
            </template>
        </div> 

        <div class="custom-facility__row-options__col custom-facility__row-options__col--b">
          <template v-for="(column, index) in customFacilityProfileColumns">
            <custom-column 
                v-if="index === 1"
                :key="column.id"
                :id="column.id"
                :index="index"              
                :name="column.name"
                :abbrev="column.abbrev"
                :options="column.options"
                :existing-columns="extraColumns"
                :ref="'custom-column-' + column.id"
                @addColumn="addColumn"
                @updateColumn="updateColumn"
                @removeColumn="removeColumn"
                @shallowTable="collapseTable"
              />
            </template>
        </div>
      </div>

      <button @click="setCustomFacilityProfile()" id="btn-save-custom" class="visuallyhidden">Save</button>
    </div>
  </div>
</template>

<script>
import router from '../../../router'
import moment from 'moment'
import _ from 'lodash'
import CustomFacilityProfileColumn from './CustomFacilityProfileColumn.vue'
import CustomFacilityProfileOptions from './CustomFacilityProfileOptions.vue'
import swal from 'sweetalert'
import { formatNumber, isEmpty } from '../../../../common/helpers'

const profileCategoryTier2 = {
  facilityType: null,
  facilityCheckboxOptions: []
}

export default {
  components: {
    'custom-column': CustomFacilityProfileColumn,
    'profile-options': CustomFacilityProfileOptions
  },
  data () {
    return {
      profile: [],
      extraColumns: [],
      addExtraColumns: false,
      profileName: '',
      profileCategory: {
        tier1: '',
        tier2: Object.assign({}, profileCategoryTier2)
      },
      customFacilityProfileEdited: false,
      profileSubmitted: false,
      maxUsageSingle: 1000000000,
      initialPricingRequestStatus: '',
      maxNameLength: 50,
      repeatMonthlyValues: true,
      enterOnPeakUsagePercent: false,
    }
  },
  computed: {
    /**
     * Legacy tier2 objects do not contain the "parents" property
     * in the profileCategory.tier2.facilityType object.
     * 
     * Legacy requests do not support auto-populating on-peak usage 
     * as a percentage *based on selected profile*, since all the
     * necessary tier2 data is not available to do so. But once they 
     * update the custom profile by selecting the tier2 selections,
     * then the *based on selected profile* functionality is available.
     */
    isLegacyCustomProfile() {
      return this.profileCategory &&
             this.profileCategory.tier2 && 
             this.profileCategory.tier2.facilityType && 
             ! this.profileCategory.tier2.facilityType.parents
    },
    hasTierTwoSelections() {
      return this.profileCategory.tier2.facilityCheckboxOptions.length > 0
    },
    hasPendingChanges() {
      return this.tempCustomFacilityProfileSet
    },
    hasChangedAnOnPeakUsagePercentValue() {
      return this.currentPricingRequest.userHasChangedACustomFacilityProfilOnPeakUsagePercentValue
    },
    currentStep() {
      return this.$store.getters.customFacilityProfileUiStep
    },
    collapseCustomFacilityProfileTable() {
      return this.$store.getters.collapseCustomFacilityProfileTable
    },
    currentPricingRequest() {
      return this.$store.getters.currentPricingRequest
    },
    pricingRequestStatus() {
      return this.$store.getters.pricingRequestStatus
    },
    facilityProfiles() {
      return this.$store.getters.facilityProfiles
    },
    facilityProfileName() {
      return this.$store.getters.facilityProfileName
    },
    customFacilityProfileCategory() {
      return this.currentPricingRequest.customFacilityProfileCategory
    },
    startMonth() {
      return this.currentPricingRequest.startMonth
    },
    termLength() {
      return this.$store.getters.termLength
    },
    peakDemandValueCount() {
      let values = this.profile.filter(obj => {
        return obj.peakDemand != ''
      })
      return values.length
    },
    skipFinalPeakDemandCheck() {
      if (this.peakDemandValueCount === this.profile.length) {
        return true
      }
      return false
    },
    activeTerms() {
      let activeTerms = this.termLength
      activeTerms = activeTerms.filter(item => {
        return item.value != ''
      })
      return activeTerms
    },
    termValues() {
      if (this.customFacilityProfileSet) {
        let values = []

        for (let i = 0; i < this.activeTerms.length; ++i) {
          // look at each term length (12, 24, etc)

          values[i] = { 
            usage: 0,
            termLength: this.activeTerms[i].value
          }

          for (let n = 0; n < this.activeTerms[i].value; ++n) {
            // go through this loop once for each digit in the term length we're looking at, so 12 times for a 12 month term
            // take the current months usage, and add it to the value total
            values[i].usage = values[i].usage +  this.customFacilityProfile.months[n].usage
          }
        }

        return values
      }
    },
    utilityId() {
      return this.currentPricingRequest.utilityMeta.id
    },
    customFacilityProfile() {
      return this.$store.getters.customFacilityProfile
    },
    tempCustomFacilityProfile() {
      return this.$store.getters.tempCustomFacilityProfile
    },
    customFacilityProfileSet() {
      if (Object.keys(this.customFacilityProfile).length > 0) {
        return true
      }
      return false
    },
    tempCustomFacilityProfileSet() {
      if (Object.keys(this.tempCustomFacilityProfile).length > 0) {
        return true
      }
      return false
    },
    customFacilityProfileColumns() {
      return this.$store.getters.customFacilityProfileColumns
    },
    customFacilityVolume() {
      return this.$store.getters.customFacilityVolume
    },
    customFacilityProfileView() {
      return this.$store.getters.customFacilityProfileView
    },
    activeButton() {
      if (this.profile.length < 1) {
        // Disabled if no profile fields have been generated
        return false
      }

      for (let i = 0; i < this.profile.length; ++i) {

        // Usage must be a positive integer.
        if (isNaN(this.profile[i].usage) || isEmpty(this.profile[i].usage) || this.profile[i].usage <= 0) {
          return false

        } else if (this.profile[i].usage > 0) {

          // On Peak Usage must be a whole number.
          if (isNaN(this.profile[i].onPeakUsage) || isEmpty(this.profile[i].onPeakUsage) || this.profile[i].onPeakUsage < 0) {
            return false
          }

          // Peak Demand must be a positive integer.
          if (isNaN(this.profile[i].peakDemand) || isEmpty(this.profile[i].peakDemand) || this.profile[i].peakDemand <= 0) {
            return false
          }
        }
      }

      if (this.extraColumns.length === 0) {
        return true

      } else {
        // Disabled if any extra column fields are empty
        for (let i = 0; i < this.extraColumns.length; ++i) {
          for (let x = 0; x < this.extraColumns[i].months.length; ++x) {

            if (this.extraColumns[i].months[x].value === 0) {
              continue
            }

            if (!this.extraColumns[i].months[x].value) {
              return false
            }
          }
        }
        return true
      }
    },
    longestTermLength() {
      const longestTerm = this.activeTerms.sort(function(a, b) {
        if (parseInt(a.value) < parseInt(b.value)) {
          return 1
        }
        return -1
      })

      return (longestTerm && longestTerm[0]) ? longestTerm[0].value : null
    },
    monthRange() {
      let startMonth = new moment(this.startMonth)
      let endMonth = new moment(this.startMonth).add(this.longestTermLength, 'months').date(0)

      let months = [];

      while (endMonth > startMonth) {
        months.push(startMonth.format('YYYY-MM-DD'))
        startMonth.add(1,'month')
      }

      return months
    },
    profileTemplate() {
      let months = []

      for (let i = 0; i < this.monthRange.length; ++i) {
        let month = {
          month: this.monthRange[i],
          usage: '',
          onPeakUsage: '',
          offPeakUsage: '',
          peakDemand: ''
        }

        months.push(month)
      }

      return months
    },
    calculatedProfile() {
      let profile = JSON.parse(JSON.stringify(this.profile))

      profile.map(function(item) {
        if (item.usage) {
          item.offPeakUsage = item.usage

          if (item.onPeakUsage) {
            item.offPeakUsage = item.usage - item.onPeakUsage
          }
        }
      })

      return profile
    },
    maxUsageTotal() {
      if (this.longestTermLength >= 1 && this.longestTermLength <= 12) {
        return 4000000000
      }

      if (this.longestTermLength >= 13 && this.longestTermLength <= 24) {
        return 8000000000
      }

      if (this.longestTermLength >= 25 && this.longestTermLength <= 36) {
        return 12000000000
      }

      if (this.longestTermLength >= 37 && this.longestTermLength <= 48) {
        return 16000000000
      }

      if (this.longestTermLength >= 49 && this.longestTermLength <= 60) {
        return 20000000000
      }
    },
    total() {
      let total = {
        usage: 0,
        onPeakUsage: 0,
        offPeakUsage: 0,
      }

      for (let i = 0; i < this.calculatedProfile.length; ++i) {
        if (this.calculatedProfile[i].usage) {
          total.usage = total.usage + parseInt(this.calculatedProfile[i].usage)
        }

        if (this.calculatedProfile[i].onPeakUsage) {
          total.onPeakUsage = total.onPeakUsage + parseInt(this.calculatedProfile[i].onPeakUsage)
        }

        if (this.calculatedProfile[i].offPeakUsage) {
          total.offPeakUsage = total.offPeakUsage + parseInt(this.calculatedProfile[i].offPeakUsage)
        }
      }

      return total
    }
  },
  watch: {
    enterOnPeakUsagePercent(value) {
      this.$store.dispatch('setCustomFacilityProfileUiOnPeakUsagePercentChoice', value)
    },
    repeatMonthlyValues(value) {
      this.$store.dispatch('setCustomFacilityProfileUiRepeatMonthlyValues', value)
    }
  },
  methods: {
    /**
     * Event bus for profile option selections.
     * 
     * Here we store the last selected radio button,
     * along with any of it's sub-checkbox selections.
     */
    profileOptionsBus(data) {

      // Un-check the "Auto-populate ..." checkbox so that the user will need to 
      // re-check it in order to reload data based on the current tier 1 and 2 selections.
      this.setOnPeakUsagePercentSelection(false)

      // Make a clone here, because at some point `this.profileCategory.tier2` seems to 
      // become a reference to a value in the Vuex store, and for the life of me I can't 
      // figure out where/how. So this avoids the "[vuex] do not mutate vuex store state 
      // outside mutation handlers" error.
      let tempTier2 = _.cloneDeep(this.profileCategory.tier2)

      // Checkbox
      if (data.checkbox) {

        // checked option
        if (data.val) {
          tempTier2.facilityCheckboxOptions.push(data.profile)

        // unchecked option
        } else {
          tempTier2.facilityCheckboxOptions = tempTier2.facilityCheckboxOptions.filter(option => option.id !== data.id)
        }

        this.profileCategory.tier2 = tempTier2

      // Radio
      } else {
        tempTier2.facilityType = data.profile
        tempTier2.facilityCheckboxOptions = []

        this.profileCategory.tier2 = tempTier2
      }
    },
    clearProfileCategoryTier2() {
      this.profileCategory.tier2 = Object.assign({}, profileCategoryTier2)

      // Un-check the "Auto-populate ..." checkbox so that the user will need to 
      // re-check it in order to reload data based on the current tier 1 and 2 selections.
      this.setOnPeakUsagePercentSelection(false)
    },
    setOnPeakUsagePercentSelection(value) {
      // If the user has already entered one or more values for On-Peak Usage 
      // before enabling the "Auto-populate ..." feature, then warn them that
      // their values will be overwritten if they proceed.
      let showWarning = false

      if (value) {
        for (let i = 0; i < this.profile.length; i++) {
          if (
            this.profile[i].onPeakUsage &&
            this.profile[i].onPeakUsage != ''
          ) {
            showWarning = true
            break
          }
        }
      }

      if (! showWarning) {
        this.enterOnPeakUsagePercent = value
        if (value) {
          this.setDefaultOnPeakUsagePercents(true)
          this.$refs['enter-on-peak-usage-percent'].checked = true
        } else {
          this.$refs['enter-on-peak-usage-percent'].checked = false
        }

      } else {
        let swalContent = document.createElement('div')
        swalContent.innerHTML = `
          <p class="align-l italic">This action will override any current On-Peak Usage values, with ones based on the selected profile.</p>
          <p class="align-l italic">Do you want to continue?</p>`

        swal({
          title: '',
          content: swalContent,
          closeOnEsc: false,
          closeOnClickOutside: false,
          buttons: {
            cancel: {
              text: "Cancel",
              value: 'cancel',
              visible: true,
              className: "btn-danger",
              closeModal: true,
            },
            yes: {
              text: "Yes",
              value: 'yes',
              visible: true,
              className: "btn-primary",
              closeModal: true,
            }
          }
        }).then((willConfirm) => {

          switch (willConfirm) {
            case 'yes':
              this.enterOnPeakUsagePercent = true
              this.setDefaultOnPeakUsagePercents(true)
              break

            case 'cancel':
            default:
              this.$refs['enter-on-peak-usage-percent'].checked = false
              break
          }
        })
      }
    },
    populateDefaultColumns() {
      for (let i = 0; i < this.profile.length; ++i) {

        if (!isEmpty(this.profile[i].usage)) {
          this.$refs['totalUsage-'+i][0].value = formatNumber(this.profile[i].usage, 0)
        }

        if (!isEmpty(this.profile[i].onPeakUsagePercent)) {
          this.$refs['onPeakUsagePercent-'+i][0].value = formatNumber(this.profile[i].onPeakUsagePercent, 0)
        }

        if (this.profile[i].usage > 0) {

          if (!isEmpty(this.profile[i].onPeakUsage)) {
            this.$refs['onPeakUsage-'+i][0].value = formatNumber(this.profile[i].onPeakUsage, 0)
          }

          if (!isEmpty(this.profile[i].peakDemand)) {
            this.$refs['peakDemand-'+i][0].value = formatNumber(this.profile[i].peakDemand, 2)
          }
        }
      }
    },
    populateExtraColumns() {
      for (let i = 0; i < this.extraColumns.length; ++i) {
        for (let x = 0; x < this.profile.length; ++x) {

          if (this.extraColumns[i].months[x].value > 0) {
            this.$refs['extra_'+this.extraColumns[i].id+'-'+x][0].value = formatNumber(this.extraColumns[i].months[x].value, 2)

          } else {
            this.$refs['extra_'+this.extraColumns[i].id+'-'+x][0].value = ''
          }
        }
      }
    },
    addColumn(column) {
      let months = []

      switch(column.type) {
        case 'multiple_values':

          // console.log(column.valueRanges)

          // Loop through months in this.monthRange
          // for each month, loop through ranges in column.valueRanges
          // if the month is within the value range, grab the value 
          // and apply it to the month.
          for (let i = 0; i < this.monthRange.length; i++) {

            // Manually construct a new Date object to avoid the issue of the date being the day before
            // the intended date when using new Date(this.monthRange[i])
            let currentMonthParts = this.monthRange[i].match(/(\d+)/g)
            let currentMonth = new Date(currentMonthParts[0], currentMonthParts[1] - 1, currentMonthParts[2])

            let month = {}

            for (let y = 0; y < column.valueRanges.length; y++) {

              if (moment(currentMonth).add(1, 'minute').isBetween(column.valueRanges[y].startMonth, column.valueRanges[y].endMonth)) {
                month = {
                  month: this.monthRange[i],
                  value: parseFloat(column.valueRanges[y].value)
                }
              }
            }

            if (!month.value && !month.moth) {
              month = {
                month: this.monthRange[i],
                value: 0
              }
            }

            months.push(month)
            this.$refs['custom-column-' + column.id][0].hideModal()
          }
          break

        default:
          for (let i = 0; i < this.monthRange.length; i++) {
            
            let month = {
              month: this.monthRange[i],
              value: parseFloat(column.defaultValue)
            }

            months.push(month)
          }
          break
      }

      if (months.length > 0) {
        this.extraColumns.push({
          id: column.id, 
          name: column.name, 
          abbrev: column.abbrev, 
          type: column.type,
          months: months,
          valueRanges: column.valueRanges
        })

        this.$store.dispatch('clearPricingRequestStatus')
        this.customFacilityProfileEdited = true 
        this.profileSubmitted = false

        setTimeout(() => {
          this.populateExtraColumns()
        }, 100)
      }
    },
    updateColumn(column) {
      let targetColumn = this.getExtraColumn('id', column.id)
      let targetIndex = this.extraColumns.indexOf(targetColumn)
      let months = []

      switch(column.type) {
        case 'multiple_values':

          // Loop through months in this.monthRange
          // for each month, loop through ranges in column.valueRanges
          // if the month is within the value range, grab the value 
          // and apply it to the month.
          for (let i = 0; i < this.monthRange.length; i++) {

            // Manually construct a new Date object to avoid the issue of the date being the day before
            // the intended date when using new Date(this.monthRange[i])
            let currentMonthParts = this.monthRange[i].match(/(\d+)/g)
            let currentMonth = new Date(currentMonthParts[0], currentMonthParts[1] - 1, currentMonthParts[2])

            let month = {}

            for (let y = 0; y < column.valueRanges.length; y++) {
              if (moment(currentMonth).add(1, 'minute').isBetween(column.valueRanges[y].startMonth, column.valueRanges[y].endMonth)) {
                month = {
                  month: this.monthRange[i],
                  value: parseFloat(column.valueRanges[y].value)
                }
              } 
            }

            if (!month.value && !month.moth) {
              month = {
                month: this.monthRange[i],
                value: null
              }
            }

            months.push(month)
            this.$refs['custom-column-' + column.id][0].hideModal()
          }
          break

        default:
          for (let i = 0; i < this.monthRange.length; i++) {
            let month = {
              month: this.monthRange[i],
              value: parseFloat(column.defaultValue)
            }

            months.push(month)
          }
          break
      }

      this.extraColumns[targetIndex].months = months
      this.extraColumns[targetIndex].valueRanges = column.valueRanges
      this.extraColumns[targetIndex].type = column.type

      this.profileSubmitted = false

      setTimeout(() => {
        this.populateExtraColumns()
      }, 100)
    },
    removeColumn(id) {
      let targetColumn = this.getExtraColumn('id', id)
      let targetIndex = this.extraColumns.indexOf(targetColumn)

      this.extraColumns.splice(targetIndex, 1)
      this.profileSubmitted = false
    },
    getExtraColumn(key, value) {
      for (var i = 0; i < this.extraColumns.length; i++) {
        if (this.extraColumns[i][key] === value) {
          return this.extraColumns[i]
        }
      }
      return null;
    },
    updateExtraColumnValue(id, index, event) {
      let value = formatNumber(event.target.value, 2, false)

      if (!value) {
        value = 0
      }

      if (value != null) {
        let targetColumn = this.getExtraColumn('id', id)
        let targetIndex = this.extraColumns.indexOf(targetColumn)
        let months = this.extraColumns[targetIndex].months

        months[index].value = parseFloat(value)

        this.extraColumns[targetIndex]['months'] = months

        this.$store.dispatch('clearPricingRequestStatus')
        this.customFacilityProfileEdited = true 
        this.profileSubmitted = false
      }
    },
    setUsage(index, value) {

      // Only allow numeric input.
      if (isNaN(value.toString().substr(-1))) {
        this.$refs['totalUsage-' + index][0].value = value.slice(0, -1)
        return
      }

      value = value.toString().replace(/\D/g,'')
      let monthStart = moment(this.profile[index].month)

      if (parseInt(value) > this.maxUsageSingle) {

        this.$store.dispatch('setNotification', {
          title: 'Month: ' + monthStart.format("MMMM YYYY"),
          type: 'error',
          msg: 'Total Usage (kWh) for a single month cannot exceed ' + formatNumber(this.maxUsageSingle, 0) + ' (kWh).' 
        })

        this.profile[index].usage = ''
        this.$refs['totalUsage-' + index][0].value = ''

        return
      }

      if (this.total.usage > this.maxUsageTotal) {

        this.$store.dispatch('setNotification', {
          title: 'Month: ' + monthStart.format("MMMM YYYY"),
          type: 'error',
          msg: 'Total Usage (kWh) for all months cannot exceed ' + formatNumber(this.maxUsageTotal, 0) + ' (kWh).' 
        })

        this.profile[index].usage = ''
        this.$refs['totalUsage-' + index][0].value = ''

        return
      }

      if (parseInt(value) != this.profile[index].usage) {

        this.profile[index].usage = parseInt(value)
        this.profile[index].onPeakUsage = ''
        this.profile[index].offPeakUsage = ''
        this.profile[index].peakDemand = ''  

        this.$refs['onPeakUsage-' + index][0].value = ''
        this.$refs['peakDemand-' + index][0].value = ''

        this.setTempCustomFacilityProfile()
        this.$store.dispatch('clearPricingRequestStatus')
        this.customFacilityProfileEdited = true    
        this.profileSubmitted = false

        // Set the value of the actual <input>, as it won't
        // happen automatically when setting the value of 
        // this.profile[index].usage
        this.$refs['totalUsage-' + index][0].value = formatNumber(value, 0)

        // Repeating the first 12 monthly values?
        if (
          this.repeatMonthlyValues && 
          index < 12
        ) {
          let mn = 12 + index
          while (mn < this.longestTermLength) {
            this.setUsage(mn, value)
            mn += 12
          }
        }
      }

      // If On-Peak Usage is entered as a percentage of the Usage,
      // then update On-Peak Usage based on the newly entered Usage.
      if (this.enterOnPeakUsagePercent) {
        this.setOnPeakUsagePercent(index)
      }
    },
    setOnPeakUsagePercent(index, value) {

      if (value === '') {
        value = 0
      }

      if (value !== undefined) {
        this.$store.dispatch('setUserHasChangedACustomFacilityProfileOnPeakUsagePercentValue', true)

        this.profile[index].onPeakUsagePercent = parseInt(value)
        this.$refs['onPeakUsagePercent-' + index][0].value = parseInt(value)

        // Max. value is 100 (%).
        if (value > 100) {
          this.profile[index].onPeakUsagePercent = 100
          this.$refs['onPeakUsagePercent-' + index][0].value = 100
        }

        // Repeating the first 12 monthly values?
        if (
          this.repeatMonthlyValues && 
          index < 12
        ) {
          let mn = 12 + index
          while (mn < this.longestTermLength) {
            this.setOnPeakUsagePercent(mn, value)
            mn += 12
          }
        }
      }

      // Set the On-Peak Usage from the entered percentage.
      if (
        !isEmpty(this.profile[index].onPeakUsagePercent) &&
        !isEmpty(this.profile[index].usage)
      ) {
        const onPeakUsage = parseInt(((this.profile[index].onPeakUsagePercent / 100) * parseInt(this.profile[index].usage)).toFixed())

        this.setOnPeakUsage(index, onPeakUsage)
      }
    },
    /**
     * Set the default "On Peak Usage %" values.
     */
    setDefaultOnPeakUsagePercents(overrideExistingValues) {
      for (let i = 0; i < this.profile.length; i++) {

        // Only set the default value if the current value is empty,
        // or if the usage value is empty, or if overrideExistingValues 
        // is set to true.
        if (
          isEmpty(this.profile[i].onPeakUsagePercent) ||
          isEmpty(this.profile[i].usage) ||
          overrideExistingValues === true
        ) {

          if (
            this.currentPricingRequest && 
            this.currentPricingRequest.customFacilityProfileCategory && 
            this.currentPricingRequest.customFacilityProfileCategory.data
          ) {
            let monthData = this.currentPricingRequest.customFacilityProfileCategory.data.find(profileDataMonth => {
              return profileDataMonth.month == ((new Date(this.profile[i].month + ' 00:00:00')).getMonth() + 1)
            })

            if (monthData) {
              this.setOnPeakUsagePercent(i, parseFloat(monthData.monthly_usage_on_peak) * 100)
            }
          }

          // Reset this to false.
          this.$store.dispatch('setUserHasChangedACustomFacilityProfileOnPeakUsagePercentValue', false)
        }
      }
    },
    setOnPeakUsage(index, value) {
      value = value.toString().replace(/\D/g,'')
      let monthStart = moment(this.profile[index].month)

      if (parseInt(value) <= parseInt(this.profile[index].usage)) {
        this.profile[index].onPeakUsage = parseInt(value)
        this.setTempCustomFacilityProfile()
        this.$store.dispatch('clearPricingRequestStatus')
        this.customFacilityProfileEdited = true 
        this.profileSubmitted = false

        // Set the value of the actual <input>, as it won't
        // happen automatically when setting the value of 
        // this.profile[index].onPeakUsage
        this.$refs['onPeakUsage-' + index][0].value = formatNumber(value, 0)

        // Repeating the first 12 monthly values?
        if (
          this.repeatMonthlyValues && 
          index < 12
        ) {
          let mn = 12 + index
          while (mn < this.longestTermLength) {
            this.setOnPeakUsage(mn, value)
            mn += 12
          }
        }

      } else {

        this.profile[index].onPeakUsage = ''
        this.profile[index].offPeakUsage = ''

        this.$store.dispatch('setNotification', { 
          title: 'Month: ' + monthStart.format("MMMM YYYY"),
          type: 'error',
          msg: 'On-peak Usage (kWh) cannot exceed Usage (kWh).' 
        })

        this.$store.dispatch('clearPricingRequestStatus')
        this.$refs['onPeakUsage-' + index][0].value = ''
      }
    },
    setPeakDemand(index, value, setInputValue) {

      // Strip everything but numbers and a decimal.
      value = value.toString().replace(/[^0-9.]/g,'')

      this.profile[index].peakDemand = parseFloat(value)
      this.setTempCustomFacilityProfile()
      this.$store.dispatch('clearPricingRequestStatus')
      this.customFacilityProfileEdited = true 
      this.profileSubmitted = false

      // Set the value of the actual <input>, as it won't
      // happen automatically when setting the value of 
      // this.profile[index].peakDemand
      if (setInputValue) {
        this.$refs['peakDemand-' + index][0].value = formatNumber(value, 2)
      }

      // Repeating the first 12 monthly values?
      if (
        this.repeatMonthlyValues && 
        index < 12
      ) {
        let mn = 12 + index
        while (mn < this.longestTermLength) {
          this.setPeakDemand(mn, value, true)
          mn += 12
        }
      }
    },
    validatePeakDemand(index, value, forceValidation) {
      // Set the peak demand on input, and validate it on blur
      // if skipFinalPeakDemandCheck, then skip the validation step.

      if (forceValidation == null) {
        forceValidation = false
      }

      value = formatNumber(value, 2, false)

      let monthStart = moment(this.profile[index].month)
      let monthEnd = monthStart.clone().endOf('month')

      let monthHours = monthEnd.diff(monthStart, 'hours', true)
      monthHours = Math.ceil(monthHours)

      let minValue = Math.ceil(this.profile[index].usage / monthHours)

      if (parseFloat(value) >= minValue) {

        this.profile[index].peakDemand = parseFloat(value)
        this.$store.dispatch('clearPricingRequestStatus')
        this.customFacilityProfileEdited = true 
        this.profileSubmitted = false

        return true

      } else if (this.skipFinalPeakDemandCheck && !forceValidation) {
        this.profile[index].peakDemand = parseFloat(value)
        this.$store.dispatch('clearPricingRequestStatus')
        this.customFacilityProfileEdited = true 
        this.profileSubmitted = false

        return true

      } else {
 
        this.profile[index].peakDemand = null

        this.$store.dispatch('setNotification', { 
          title: 'Month: ' + monthStart.format("MMMM YYYY"),
          type: 'error', 
          msg: 'Peak Demand (KW) must be at least equal to ' + minValue + ' (KW). (Usage (kWh) ÷ hours in ' + monthStart.format("MMM 'YY") + ').'
        })

        this.$refs['peakDemand-' + index][0].value = ''

        return false
      }
    },
    setCustomFacilityProfile(haltRouter) {
      this.profileSubmitted = true;

      // Set from temp data in store, since the local component 
      // data will be empty when this method is triggered by 
      // vue-router route guard.
      if (this.tempCustomFacilityProfileSet) {
        this.$store.dispatch('setCustomFacilityProfile', {
          months: this.tempCustomFacilityProfile.months,
          total: this.tempCustomFacilityProfile.total,
          extraColumns: this.tempCustomFacilityProfile.extraColumns
        })

        this.$store.dispatch('clearTempCustomFacilityProfile')

      // No temp data in store, so set from local component data.
      } else {
        this.$store.dispatch('setCustomFacilityProfile', {
          months: this.calculatedProfile,
          total: this.total,
          extraColumns: this.extraColumns
        })
      }

      // This makes sure that the volume is updated in the Nav-Input component.
      this.updateCustomVolume()

      if (! haltRouter) {
        router.push({ name: 'Volume', params: { skipWarning: true }})
      }
    },
    setTempCustomFacilityProfile() {
      this.$store.dispatch('setTempCustomFacilityProfile', {
        months: this.calculatedProfile,
        total: this.total,
        extraColumns: this.extraColumns
      })
    },
    clearCustomFacilityProfile() {
      document.querySelectorAll('.numeric-input, .numeric-input-decimal').forEach(inpt => {
        inpt.value = ''
      })
      
      this.profile = []

      setTimeout(() => {
        this.loadCustomFacilityProfileTemplate()
      }, 10)

      this.extraColumns = []

      if (this.tempCustomFacilityProfileSet) {
        this.$store.dispatch('clearTempCustomFacilityProfile')
      }

      if (this.customFacilityProfileSet) {
        this.$store.dispatch('clearCustomFacilityProfile')
      }

      this.$store.dispatch('clearPricingRequestStatus')
      this.customFacilityProfileEdited = false 
      this.profileSubmitted = false
    },
    updateCustomVolume() {
      this.$store.dispatch('clearVolume')

      if (this.customFacilityVolume.terms && this.customFacilityVolume.terms.length) {

        let brokerFee = this.customFacilityVolume.brokerFee

        this.$store.dispatch('setCustomFacilityVolume', {
          terms: this.termValues,
          brokerFee: brokerFee
        })
      }
    },
    setFacilityProfileMeta(name) {
      this.$store.dispatch('setFacilityProfileName', this.profileName)
      this.$store.dispatch('setCustomFacilityProfileUiStep', 'profile')

      // Flag that a legacy-format tier 2 was updated so that we can now use 
      // the profile info to auto-populate On-Peak Usage as a percentage.
      if (this.profileCategory.tier2.wasLegacyFormat) {
        this.profileCategory.tier2.wasUpdated = true
      }

      this.$store.dispatch('setCustomFacilityProfileCategory', this.profileCategory)
        .then(() => {
          this.setDefaultOnPeakUsagePercents()
        })
    },
    loadCustomFacilityProfileTemplate() {
      let template = []

      for (let i = 0; i < this.profileTemplate.length; ++i) {
        let templatePart = {
          month: this.profileTemplate[i].month,
          usage: this.profileTemplate[i].usage,
          onPeakUsage: this.profileTemplate[i].onPeakUsage,
          onPeakUsagePercent: this.profileTemplate[i].onPeakUsagePercent,
          offPeakUsage: this.profileTemplate[i].offPeakUsage,
          peakDemand: this.profileTemplate[i].peakDemand
        }

        template.push(templatePart)
      }

      this.profile = template
    },
    loadStoreProfile() {
      let setProfile = this.customFacilityProfile.months
      let profile = []

      for (let i = 0; i < setProfile.length; ++i) {
        let month = {
          month: setProfile[i].month,
          usage: setProfile[i].usage,
          onPeakUsage: setProfile[i].onPeakUsage,
          onPeakUsagePercent: setProfile[i].onPeakUsagePercent,
          offPeakUsage: setProfile[i].offPeakUsage,
          peakDemand: setProfile[i].peakDemand
        }

        profile.push(month)
      }

      this.profile = profile

      if (Object.keys(this.customFacilityProfile.extraColumns).length > 0) {
        let setExtraColumns = this.customFacilityProfile.extraColumns
        let extraColumns = []

        for (let i = 0; i < setExtraColumns.length; ++i) {
          let column = {
            id: setExtraColumns[i].id, 
            name: setExtraColumns[i].name, 
            abbrev: setExtraColumns[i].abbrev, 
            type: setExtraColumns[i].type,
            months: setExtraColumns[i].months,
            valueRanges: setExtraColumns[i].valueRanges
          }

          extraColumns.push(column)
        }

        this.extraColumns = extraColumns
      }
    },
    loadTempStoreProfile() {
      let setProfile = this.tempCustomFacilityProfile.months
      let profile = []

      for (let i = 0; i < setProfile.length; ++i) {
          let month = {
            month: setProfile[i].month,
            usage: setProfile[i].usage,
            onPeakUsage: setProfile[i].onPeakUsage,
            onPeakUsagePercent: setProfile[i].onPeakUsagePercent,
            offPeakUsage: setProfile[i].offPeakUsage,
            peakDemand: setProfile[i].peakDemand
          }

          profile.push(month)
      }

      this.profile = profile

      if (Object.keys(this.tempCustomFacilityProfile.extraColumns).length > 0) {
        let setExtraColumns = this.tempCustomFacilityProfile.extraColumns
        let extraColumns = []

        for (let i = 0; i < setExtraColumns.length; ++i) {
          let column = {
            id: setExtraColumns[i].id, 
            name: setExtraColumns[i].name, 
            abbrev: setExtraColumns[i].abbrev, 
            type: setExtraColumns[i].type,
            months: setExtraColumns[i].months,
            valueRanges: setExtraColumns[i].valueRanges
          }

          extraColumns.push(column)
        }

        this.extraColumns = extraColumns
      }
    },
    displayMonth(month) {
      let displayMont = new moment(month)
      return displayMont.format("MMM 'YY")
    },
    displayValue(value) {
      let displayValue = formatNumber(value, 0)
      return displayValue
    },
    wrapUnits(str) {
      let pos = str.lastIndexOf('(')
      if (pos > -1) {
        return str.substr(0, pos) + '<br>' + str.substr(pos)
      }

      return str
    },
    inputDisabled(index, dataType) {
      // If a usage value has not be entered...
      if (
        dataType != 'usage' && 
        !this.profile[index].usage
      ) {
        return true
      }

      // If we're repeating monthly values and this is 
      // for a month beyond the first year...
      if (
        this.repeatMonthlyValues && 
        index >= 12
      ) {
        return true
      }

      // We're entering on-peak usage as a percentage...
      if (
        dataType == 'on-peak-usage' &&
        this.enterOnPeakUsagePercent
      ) {
        return true
      }

      return false
    },
    generateId(id, prefix) {
      return prefix + id
    },
    tabIndex(index) {
      return index + 1
    },
    goBack() {
      if (this.currentStep == 'profile') {
        this.$store.dispatch('setCustomFacilityProfileUiStep', 'meta')

      } else {
        if (!this.customFacilityProfileEdited) {
          this.$store.dispatch('unsetCustomFacilityProfileView')
          router.push({ name: 'FacilityProfile', params: { skipWarning: true }})
        } else {
          this.$store.dispatch('unsetCustomFacilityProfileView')
          router.push({ name: 'FacilityProfile' })
        }
      }
    },
    cleanCategory(string) {
      return string.replace('<br>', '')
    },
    collapseTable() {
      this.$store.dispatch('collapseCustomFacilityProfileTable')
    },
    expandTable() {
      this.$store.dispatch('expandCustomFacilityProfileTable')
    },
    toggleTableCollapse() {
      if (this.collapseCustomFacilityProfileTable) {
        this.expandTable()
      } else {
        this.collapseTable()
      }
    },
    focusProfileName() {
      setTimeout(() => {
        this.$refs['custom-profile-name'].focus()
      }, 10)
    },
    limitNameLength() {
      if (this.profileName.length > this.maxNameLength) {
        this.profileName = this.profileName.slice(0, this.maxNameLength)
      }
    },
    updateCustomFacilityProfile() {
      if (! this.longestTermLength || ! this.customFacilityProfile.months.length > 0) {
        return
      }

      // Transfer Month Values:
      let months = []

      for (let i = 0; i < this.monthRange.length; ++i) {
        if (i <= this.longestTermLength) {

          let previousMonth = this.customFacilityProfile.months[i]
          let month = {}

          if (previousMonth) {

            month = {
              month: previousMonth.month,
              usage: previousMonth.usage,
              onPeakUsage: previousMonth.onPeakUsage,
              offPeakUsage: previousMonth.offPeakUsage,
              peakDemand: previousMonth.peakDemand
            }

            // "onPeakUsagePercent" was added later on in app dev, so earlier
            // profiles may not have it. So, only add it to this object if it 
            // was set, otherwise the isEqual() check below will fail even if 
            // everything else is equal between the two checked objects.
            if (!isEmpty(previousMonth.onPeakUsagePercent)) {
              month.onPeakUsagePercent = previousMonth.onPeakUsagePercent
            }

          } else {

            month = {
              month: this.monthRange[i],
              usage: '',
              onPeakUsage: '',
              onPeakUsagePercent: '',
              offPeakUsage: '',
              peakDemand: ''
            }
          }

          months.push(month)
        }
      }

      // Transfer Extra Column Values:
      let previousExtraColumns = this.customFacilityProfile.extraColumns
      let extraColumns = []

      if (previousExtraColumns.length > 0) {

        for (let i = 0; i < previousExtraColumns.length; ++i) {

          let months = []
          let valueRanges = []

          for (let x = 0; x < this.monthRange.length; ++x) {

            if (i <= this.longestTermLength) {
              let previousMonth = previousExtraColumns[i].months[x]
              let month = {}

              if (previousMonth) {

                month = {
                  month: previousMonth.month,
                  value: previousMonth.value
                }
              } else {

                month = {
                  month: this.monthRange[x],
                  value: 0
                }
              }

              months.push(month)
            }
          }

          if (previousExtraColumns[i].valueRanges != null && previousExtraColumns[i].valueRanges.length > 0) {

            let newStartMonth = new moment(this.startMonth).toDate()
            let newEndMonth = new moment(this.startMonth).add(this.longestTermLength, 'months').date(0).toDate()

            for (let y = 0; y < previousExtraColumns[i].valueRanges.length; ++y) {

              // Inspect each value range
              let previousStartMonth = new moment(previousExtraColumns[i].valueRanges[y].startMonth).toDate()
              let previousEndMonth = new moment(previousExtraColumns[i].valueRanges[y].endMonth).toDate()

              // If the value range falls within the new overall range, save it
              if (moment(previousStartMonth).isBetween(newStartMonth, newEndMonth) && moment(previousEndMonth).isBetween(newStartMonth, newEndMonth)) {

                let range = {
                  value: previousExtraColumns[i].valueRanges[y].value,
                  startMonth: previousExtraColumns[i].valueRanges[y].startMonth,
                  endMonth: previousExtraColumns[i].valueRanges[y].endMonth
                }

                valueRanges.push(range)

                continue
              }

              // If the old range straddles new start month, set range start month equal to new start month
              if (moment(previousStartMonth).isBefore(newStartMonth) && moment(previousEndMonth).isAfter(newStartMonth)) {

                let range = {
                  value: previousExtraColumns[i].valueRanges[y].value,
                  startMonth: newStartMonth,
                  endMonth: previousExtraColumns[i].valueRanges[y].endMonth
                }

                valueRanges.push(range)

                continue
              }

              // If the old range straddles new end month, set range end month equal to new end month
              if (moment(previousStartMonth).isBefore(newEndMonth) && moment(previousEndMonth).isAfter(newEndMonth)) {

                let range = {
                  value: previousExtraColumns[i].valueRanges[y].value,
                  startMonth: previousExtraColumns[i].valueRanges[y].startMonth,
                  endMonth: newEndMonth
                }

                valueRanges.push(range)

                continue
              }
            }
          }

          extraColumns[i] = {
            id: previousExtraColumns[i].id, 
            name: previousExtraColumns[i].name, 
            abbrev: previousExtraColumns[i].abbrev, 
            type: previousExtraColumns[i].type,
            months: months,
            valueRanges: valueRanges
          }
        }
      }

      // Recalculate Total:
      let total = {
        usage: 0,
        onPeakUsage: 0,
        offPeakUsage: 0
      }

      for (let i = 0; i < months.length; ++i) {
        if (months[i].usage) {
          total.usage = total.usage + parseInt(months[i].usage)
        }

        if (months[i].onPeakUsage) {
          total.onPeakUsage = total.onPeakUsage + parseInt(months[i].onPeakUsage)
        }

        if (months[i].offPeakUsage) {
          total.offPeakUsage = total.offPeakUsage + parseInt(months[i].offPeakUsage)
        }
      }

      // console.log(_.omit(this.customFacilityProfile, ['extraColumns']))
      // console.log({ months: months, total: total })

      // Update Profile (if it has changed):
      if (!_.isEqual(_.omit(this.customFacilityProfile, ['extraColumns']), { months: months, total: total })) {
        this.$store.dispatch('setCustomFacilityProfile', {
          months: months,
          total: total,
          extraColumns: extraColumns
        })
      }
    },
    prepopulateRows() {
      if (this.repeatMonthlyValues && this.longestTermLength > 12) {

        // Loop through the first 12 months of custom profile rows...
        for (let index = 0; index < 12; index++) {

          let mn = 12 + index

          // Then use the data from the first 12 months 
          // to set the values for any empty values beyond  
          // the first 12 months.
          while (mn < this.longestTermLength) {
            if (this.profile[index].usage && this.profile[mn].usage == '') {
              this.setUsage(mn, this.profile[index].usage)
            }

            if (this.profile[index].onPeakUsage && this.profile[mn].onPeakUsage == '') {
              this.setOnPeakUsage(mn, this.profile[index].onPeakUsage)
            }

            if (this.profile[index].peakDemand && this.profile[mn].peakDemand == '') {
              this.setPeakDemand(mn, this.profile[index].peakDemand, true)
            }

            mn += 12
          }
        }
      }
    }
  },
  created() {
    if (this.pricingRequestStatus) {
      this.initialPricingRequestStatus = _.cloneDeep(this.pricingRequestStatus)
    }

    if (this.utilityId) {
      this.$store.dispatch('fetchFacilityProfiles', this.utilityId)
      this.$store.dispatch('fetchCustomFacilityProfileColumns', this.utilityId)
    }

    if (this.facilityProfileName) {
      this.profileName = this.facilityProfileName
    }

    if (this.customFacilityProfileCategory.tier1 && this.customFacilityProfileCategory.tier2) {
      this.profileCategory = {
        tier1: '',
        tier2: {}
      }
      this.profileCategory = {
        tier1: {
          name: this.customFacilityProfileCategory.tier1.name,
          displayName: this.customFacilityProfileCategory.tier1.displayName,
          id: this.customFacilityProfileCategory.tier1.id,    
          index: this.customFacilityProfileCategory.tier1.index
        },
        tier2: Object.assign({}, this.customFacilityProfileCategory.tier2)
      }
    }

    if (this.customFacilityProfileSet) {

      this.$store.dispatch('setCustomFacilityProfileUiStep', 'profile')
      this.updateCustomFacilityProfile()
      this.loadStoreProfile()
      this.customFacilityProfileEdited = true
      this.profileSubmitted = true

    } else if (this.tempCustomFacilityProfileSet) {

      this.$store.dispatch('setCustomFacilityProfileUiStep', 'profile')
      this.loadTempStoreProfile()
      this.customFacilityProfileEdited = true
      this.profileSubmitted = false

    } else {
      this.loadCustomFacilityProfileTemplate()
    }

    // Setup UI options.
    if (this.$store.state.currentPricingRequest) {
      this.enterOnPeakUsagePercent = this.$store.state.currentPricingRequest.enterCustomFacilityProfileOnPeakUsageAsPercentage
      this.repeatMonthlyValues = this.$store.state.currentPricingRequest.setCustomFacilityProfileUiRepeatMonthlyValues
    }
  },
  mounted() {

    // Populate default column values.
    if (this.customFacilityProfileSet || this.tempCustomFacilityProfileSet) {
      this.populateDefaultColumns()
      
      if (this.customFacilityProfileSet && Object.keys(this.customFacilityProfile.extraColumns).length > 0) {
        this.populateExtraColumns()

      } else if (this.tempCustomFacilityProfileSet && Object.keys(this.tempCustomFacilityProfile.extraColumns).length > 0) {
        this.populateExtraColumns()
      }
    }

    // Populate any new custom profile rows that may have
    // been added (e.g. from updating the start month or term lengths).
    this.prepopulateRows()

    // Auto-populate On-Peak Usage as a Percentage checkbox.
    if (this.enterOnPeakUsagePercent) {

      // Check the "Auto-populate On-Peak Usage as a Percentage" checkbox, where applicable.
      this.$refs['enter-on-peak-usage-percent'].checked = true

      // Set any default On-Peak Usage percents.
      // The may be necessary if an additional term was just added.
      this.setDefaultOnPeakUsagePercents()
    }
  },
  beforeRouteLeave(to, from, next) {
    if (
      (to.params && to.params.skipWarning) || 
      ! this.customFacilityProfileSet || 
      ! this.hasPendingChanges
    ) {
      next()

    } else {
      let swalContent = document.createElement('div')
      let self = this

      swalContent.innerHTML = '<p class="alerts message alerts--no-brdr error">Do you want to save before leaving?</p></p>'

      swal({
        title: '<span class="alerts alerts--no-brdr error">Unsaved Custom Profile Values</span>',
        content: swalContent,
        closeOnEsc: false,
        closeOnClickOutside: false,
        buttons: {
          leave: {
            text: "Don’t Save",
            value: 'nosave',
            visible: true,
            className: "btn-danger",
            closeModal: true,
          },
          cancel: {
            text: "Cancel",
            value: 'cancel',
            visible: true,
            className: "btn-outline-danger",
            closeModal: true
          },
          save: {
            text: "Save",
            value: 'save',
            visible: true,
            className: "btn-primary",
            closeModal: true
          }
        }
      }).then((value) => {

        switch (value) {
          case 'cancel':
            next(false)
            break;

          case 'save':
            self.setCustomFacilityProfile(true)
            next()
            break;

          case 'nosave':
          default:
            this.$store.dispatch('clearTempCustomFacilityProfile')

            if (self.initialPricingRequestStatus && self.initialPricingRequestStatus === 'Loaded') {
              self.$store.dispatch('setPricingRequestStatus', 'Loaded')
            }
            next()
            break
        }
      })
    }
  }
}
</script>
